/**
 * Likelihood of Events generator (6.DPSR.2.1).
 *
 * Given a probability between 0 and 1, classify the event's likelihood. The
 * probability is chosen first (as an exact Rational) and the correct category
 * is derived from it, so the graded choice always matches the stated value.
 *
 *   p = 0            -> impossible
 *   0 < p < 1/2      -> unlikely
 *   p = 1/2          -> equally likely
 *   1/2 < p < 1      -> likely
 *   p = 1            -> certain
 *
 * Families:
 *   F1: probability given as a fraction
 *   F2: probability given as a decimal
 *   F3: probability given as a percent
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "likelihood" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

/** SC-friendly denominators for the probability values. */
const DENOMS: readonly number[] = [2, 4, 5, 10];

const DIFF: Record<Difficulty, readonly Family[]> = {
  easy: ["F1"],
  standard: ["F1", "F2"],
  hard: ["F1", "F2", "F3"],
  challenge: ["F2", "F3"],
};

type Category = "impossible" | "unlikely" | "equally-likely" | "likely" | "certain";

const CATEGORY_LABEL: Record<Category, string> = {
  impossible: "Impossible",
  unlikely: "Unlikely",
  "equally-likely": "Equally likely",
  likely: "Likely",
  certain: "Certain",
};

const HALF = rat(1, 2);

function classify(p: Rational): Category {
  if (p.isZero()) return "impossible";
  if (p.equals(rat(1))) return "certain";
  if (p.equals(HALF)) return "equally-likely";
  return p.lt(HALF) ? "unlikely" : "likely";
}

/** A percent that stays a clean integer percent for wording. */
function pickPercent(rng: Rng): { p: Rational; pct: number } {
  // Multiples of 5 keep the fraction and percent clean; include 0, 50, 100.
  const choices = [0, 5, 10, 20, 25, 40, 50, 60, 75, 80, 90, 100];
  const pct = rng.pick(choices);
  return { p: rat(pct, 100), pct };
}

export class LikelihoodGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const family = rng.pick(DIFF[ctx.difficulty]);

      let p: Rational;
      let valueText: string;

      if (family === "F1") {
        const d = rng.pick(DENOMS);
        const n = rng.int(0, d); // allow 0 (impossible) and d (certain)
        p = rat(n, d);
        // Present the raw fraction as worded (e.g. "3/4"), not the reduced form.
        valueText = `${n}/${d}`;
      } else if (family === "F2") {
        // Decimals with one or two places from tenths/hundredths.
        const useHundredths = rng.bool();
        const denom = useHundredths ? 100 : 10;
        const n = rng.int(0, denom);
        p = rat(n, denom);
        valueText = p.toNumber().toFixed(useHundredths ? 2 : 1);
      } else {
        const picked = pickPercent(rng);
        p = picked.p;
        valueText = `${picked.pct}%`;
      }

      const correct = classify(p);
      const correctId = correct;

      const allCategories: Category[] = [
        "impossible",
        "unlikely",
        "equally-likely",
        "likely",
        "certain",
      ];
      const choices: Choice[] = allCategories.map((c) => ({
        id: c,
        label: CATEGORY_LABEL[c],
      }));
      const shuffled = rng.shuffle(choices);

      const ids = shuffled.map((c) => c.id);
      if (!ids.includes(correctId)) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `The probability that an event happens is ${valueText}. How likely is the event?`,
        },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [
          {
            description:
              "0 is impossible, 1 is certain, and 1/2 is equally likely. Below 1/2 is unlikely; above 1/2 is likely.",
            latex: p.toLatex(),
          },
        ],
        hints: [
          { level: 1, text: "Compare the probability to 0, 1/2, and 1." },
          { level: 2, text: "Less than one half is unlikely; more than one half is likely." },
        ],
        subskills: topic.subskills,
        choices: shuffled,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const likelihood = new LikelihoodGenerator();
